import type { FilterConfigOperator } from '../../types';

/**
 * Filter value prefixes, exactly as the API's `Filterable::extractLogicalOperator` reads them
 * (uniliveapi `app/Traits/BaseModel/Filterable.php`). It checks `!` first and strips one
 * character, then `>=`, `>`, `<=`, `<`. Not-equal is `!` on the wire: the API's `$NOT = '<>'` is
 * only its internal operator name, and a value sent as `<>x` is parsed as `< '>x'`.
 */
export const FILTER_OPERATOR_NOT: FilterConfigOperator = '!';
const LTE: FilterConfigOperator = '<=';
const GTE: FilterConfigOperator = '>=';
const LT: FilterConfigOperator = '<';
const GT: FilterConfigOperator = '>';

// Two-character operators before their one-character prefixes.
const operators: FilterConfigOperator[] = [FILTER_OPERATOR_NOT, LTE, GTE, GT, LT];

/** Prefix a filter value with an operator; the inverse of `splitValueAndOperator`. */
export const joinValueAndOperator = (value: string, operator?: FilterConfigOperator | null) =>
    `${operator ?? ''}${value}`;

/**
 * Return [value, operator]
 */
export const splitValueAndOperator = (value: string): [string, FilterConfigOperator | null] => {
    const operator = operators.find(op => value.startsWith(op));

    if (operator != null) {
        return [value.slice(operator.length), operator];
    }

    return [value, null];
};

export const removeEmptyProperties = obj => {
    // eslint-disable-next-line no-restricted-syntax
    for (const key in obj) {
        if (obj[key] === '' || obj[key] === null || obj[key] === undefined) {
            // eslint-disable-next-line no-param-reassign
            delete obj[key];
        }
    }
    return obj;
};

/**
 * The day a date range filter bound refers to, ignoring its operator and any time part. A
 * filter value looks like `>=2024-03-01` or `<=2024-03-31 23:59:59`.
 */
export const dateRangeFilterBoundToDate = (bound: unknown): Date | undefined => {
    const [val] = splitValueAndOperator(String(bound ?? ''));
    if (val != null && val?.length > 0) {
        // ignore time
        return new Date(val.split(' ')[0]);
    }
    return undefined;
};

/** The filter value for a date range: from the start of the first day to the end of the last. */
export const dateRangeFilterValue = (apiStartDate: string, apiEndDate: string): string[] => [
    `>=${apiStartDate}`,
    `<=${apiEndDate} 23:59:59`,
];
